import { Input, InputProps } from '@chakra-ui/react'
import React, { useState, useCallback, useEffect } from 'react'

interface CurrencyInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  allowNegative?: boolean
  decimalPlaces?: number
  numericValue?: number
  onNumericValueChange?: (value: number) => void
}

export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  allowNegative = false,
  decimalPlaces = 2,
  numericValue = null,
  onBlur,
  onNumericValueChange,
  ...inputProps
}) => {
  const [displayValue, setDisplayValue] = useState<string>('')

  // Format number to display value with commas and decimals
  const formatDisplayValue = useCallback(
    (value: number): string => {
      if (isNaN(value)) {
        return ''
      }

      if (value == null) {
        return ''
      }

      if (value == 0) {
        return ''
      }

      const options: Intl.NumberFormatOptions = {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimalPlaces,
        useGrouping: true,
      }

      return new Intl.NumberFormat('en-US', options).format(value)
    },
    [decimalPlaces]
  )

  // Parse display value back to number
  const parseDisplayValue = useCallback((value: string): number => {
    // Remove commas and non-numeric characters except decimal point and minus sign
    const cleanValue = value.replace(/[^-\d.]/g, '')
    const parsed = parseFloat(cleanValue)
    return isNaN(parsed) ? 0 : parsed
  }, [])

  // Update display value when numericValue changes
  useEffect(() => {
    setDisplayValue(formatDisplayValue(numericValue))
  }, [numericValue])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value

    // Allow empty input
    if (inputValue === '') {
      setDisplayValue('')
      onNumericValueChange?.(0)
      return
    }

    // Parse the input to get numeric value
    const numericVal = parseDisplayValue(inputValue)

    // Check negative value constraint
    if (!allowNegative && numericVal < 0) {
      return
    }

    // Update the numeric value
    onNumericValueChange?.(numericVal)

    // Update display value with proper formatting
    setDisplayValue(formatDisplayValue(numericVal))
  }

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      // Ensure proper formatting on blur
      const numericVal = parseDisplayValue(displayValue)
      setDisplayValue(formatDisplayValue(numericVal))

      // Call original onBlur if provided
      onBlur?.(event)
    },
    [displayValue, parseDisplayValue, formatDisplayValue, onBlur]
  )

  return (
    <Input
      {...inputProps}
      autoComplete={'off'}
      inputMode={'decimal'}
      value={displayValue}
      onBlur={handleBlur}
      onChange={handleChange}
    />
  )
}
